import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  VStack,
  HStack,
  Heading,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Alert,
  AlertIcon,
  useToast,
  TableContainer,
  Select,
  Text,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon, ViewIcon, RepeatIcon } from "@chakra-ui/icons";
import { adminApiService } from "../services/adminApiService";
import { Blok, BlokCreate, BlokUpdate, Divisi, QueryParams } from "../types";

const BlokManagement: React.FC = () => {
  const [bloks, setBloks] = useState<Blok[]>([]);
  const [divisis, setDivisis] = useState<Divisi[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedBlok, setSelectedBlok] = useState<Blok | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formData, setFormData] = useState<BlokCreate>({
    code: "",
    divisi_id: 0,
    odoo_id: undefined,
    company_id: undefined,
    planted_area: undefined,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const toast = useToast();

  // Fetch bloks and divisis data
  const fetchBloks = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: QueryParams = {
        search: searchTerm || undefined,
        include_disabled: includeDisabled,
      };
      const response = await adminApiService.getBloks(params);
      setBloks(response.data);
    } catch (error: any) {
      setError("Failed to fetch bloks");
      console.error("Error fetching bloks:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDivisis = async () => {
    try {
      const response = await adminApiService.getDivisis({ include_disabled: false });
      setDivisis(response.data);
    } catch (error: any) {
      console.error("Error fetching divisis:", error);
    }
  };

  useEffect(() => {
    fetchDivisis();
  }, []);

  useEffect(() => {
    fetchBloks();
  }, [searchTerm, includeDisabled]);

  const getDivisiName = (divisiId: number) => {
    const divisi = divisis.find(d => d.id === divisiId);
    return divisi ? divisi.name : `Divisi ID: ${divisiId}`;
  };

  const handleOpenModal = (blok?: Blok) => {
    if (blok) {
      setSelectedBlok(blok);
      setFormData({
        code: blok.code,
        divisi_id: blok.divisi_id,
        odoo_id: blok.odoo_id,
        company_id: blok.company_id,
        planted_area: blok.planted_area,
      });
      setIsEditing(true);
    } else {
      setSelectedBlok(null);
      setFormData({
        code: "",
        divisi_id: 0,
        odoo_id: undefined,
        company_id: undefined,
        planted_area: undefined,
      });
      setIsEditing(false);
    }
    onOpen();
  };

  const handleSubmit = async () => {
    if (!formData.code || !formData.divisi_id) {
      toast({
        title: "Validation Error",
        description: "Code and Divisi are required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSubmitLoading(true);
    try {
      if (isEditing && selectedBlok) {
        const updateData: BlokUpdate = {
          code: formData.code,
          divisi_id: formData.divisi_id,
          odoo_id: formData.odoo_id,
          company_id: formData.company_id,
          planted_area: formData.planted_area,
        };
        await adminApiService.updateBlok(selectedBlok.id, updateData);
        toast({
          title: "Success",
          description: "Blok updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        await adminApiService.createBlok(formData);
        toast({
          title: "Success",
          description: "Blok created successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
      onClose();
      fetchBloks();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to ${isEditing ? "update" : "create"} blok: ${error.response?.data?.detail || error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error submitting blok:", error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (blok: Blok) => {
    try {
      await adminApiService.deleteBlok(blok.id);
      toast({
        title: "Success",
        description: "Blok deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchBloks();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to delete blok: ${error.response?.data?.detail || error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error deleting blok:", error);
    }
  };

  const handleRestore = async (blok: Blok) => {
    try {
      await adminApiService.restoreBlok(blok.id);
      toast({
        title: "Success",
        description: "Blok restored successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchBloks();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to restore blok: ${error.response?.data?.detail || error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error restoring blok:", error);
    }
  };

  if (loading) {
    return (
      <Box p={6} textAlign="center">
        <Text>Loading bloks...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="lg" mb={4}>Blok Management</Heading>
          
          <HStack spacing={4} mb={4}>
            <Input
              placeholder="Search bloks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              maxW="300px"
            />
            <Select
              value={includeDisabled ? "all" : "active"}
              onChange={(e) => setIncludeDisabled(e.target.value === "all")}
              maxW="200px"
            >
              <option value="active">Active Only</option>
              <option value="all">Include Disabled</option>
            </Select>
            <Button colorScheme="blue" onClick={() => handleOpenModal()}>
              Add Blok
            </Button>
          </HStack>
        </Box>

        <TableContainer>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Blok Code</Th>
                <Th>Divisi</Th>
                <Th>Planted Area</Th>
                <Th>Odoo ID</Th>
                <Th>Company ID</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {bloks.map((blok) => (
                <Tr key={blok.id}>
                  <Td>
                    <Badge colorScheme="blue" variant="solid">
                      {blok.code}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme="purple" variant="outline">
                      {getDivisiName(blok.divisi_id)}
                    </Badge>
                  </Td>
                  <Td>
                    {blok.planted_area ? (
                      <Text>{blok.planted_area.toFixed(2)} ha</Text>
                    ) : (
                      <Text color="gray.500">-</Text>
                    )}
                  </Td>
                  <Td>
                    {blok.odoo_id ? (
                      <Badge colorScheme="blue" variant="outline">
                        {blok.odoo_id}
                      </Badge>
                    ) : (
                      <Text color="gray.500">-</Text>
                    )}
                  </Td>
                  <Td>
                    {blok.company_id ? (
                      <Badge colorScheme="green" variant="outline">
                        {blok.company_id}
                      </Badge>
                    ) : (
                      <Text color="gray.500">-</Text>
                    )}
                  </Td>
                  <Td>
                    <Badge colorScheme={blok.is_disabled ? "red" : "green"}>
                      {blok.is_disabled ? "Disabled" : "Active"}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <IconButton
                        aria-label="View"
                        icon={<ViewIcon />}
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenModal(blok)}
                      />
                      {!blok.is_disabled ? (
                        <>
                          <IconButton
                            aria-label="Edit"
                            icon={<EditIcon />}
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                            onClick={() => handleOpenModal(blok)}
                          />
                          <IconButton
                            aria-label="Delete"
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            variant="outline"
                            onClick={() => handleDelete(blok)}
                          />
                        </>
                      ) : (
                        <IconButton
                          aria-label="Restore"
                          icon={<RepeatIcon />}
                          size="sm"
                          colorScheme="green"
                          variant="outline"
                          onClick={() => handleRestore(blok)}
                        />
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>

        {bloks.length === 0 && (
          <Alert status="info">
            <AlertIcon />
            No bloks found matching your criteria.
          </Alert>
        )}
      </VStack>

      {/* Blok Form Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isEditing ? "Edit Blok" : "Add New Blok"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Blok Code</FormLabel>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                  placeholder="Enter blok code"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Divisi</FormLabel>
                <Select
                  value={formData.divisi_id}
                  onChange={(e) => setFormData({...formData, divisi_id: parseInt(e.target.value)})}
                  placeholder="Select divisi"
                >
                  <option value={0} disabled>Select a divisi</option>
                  {divisis.map((divisi) => (
                    <option key={divisi.id} value={divisi.id}>
                      {divisi.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Planted Area (hectares)</FormLabel>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.planted_area || ""}
                  onChange={(e) => setFormData({...formData, planted_area: e.target.value ? parseFloat(e.target.value) : undefined})}
                  placeholder="Enter planted area (optional)"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Odoo ID</FormLabel>
                <Input
                  type="number"
                  value={formData.odoo_id || ""}
                  onChange={(e) => setFormData({...formData, odoo_id: e.target.value ? parseInt(e.target.value) : undefined})}
                  placeholder="Enter Odoo ID (optional)"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Company ID</FormLabel>
                <Input
                  type="number"
                  value={formData.company_id || ""}
                  onChange={(e) => setFormData({...formData, company_id: e.target.value ? parseInt(e.target.value) : undefined})}
                  placeholder="Enter Company ID (optional)"
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleSubmit}
              isLoading={submitLoading}
              loadingText={isEditing ? "Updating..." : "Creating..."}
            >
              {isEditing ? "Update" : "Create"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default BlokManagement;
